import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
// import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import { TextareaFieldset } from "ui/textareaFieldset";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BusFront,
  Edit2,
  Plus,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { getVehicleTypes, VehicleType } from "services/vehicleTypeMaster";
import {
  searchVehicles,
  createVehicle,
  updateVehicle,
  VehicleSearchItem,
} from "services/vehicleMasterService";
import { usePageHeader } from "contexts/PageHeaderContext";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";

type OwnershipType = "Personal" | "Outside";

type Vehicle = {
  id: string;
  registrationNumber: string;
  make: string;
  model: string;
  color: string;
  fuelType: string;
  year: number;
  vehicleType: string;
  vehicleTypePublicId: string;
  ownershipType: OwnershipType;
  occupancy: number;
  chassisNumber?: string;
  registrationDate?: string;
  registrationExpiry?: string;
  purchaseYear?: number;
  notes: string;
  active: boolean;
};

const emptyVehicle = (): Vehicle => ({
  id: "",
  registrationNumber: "",
  make: "",
  model: "",
  color: "",
  fuelType: "",
  year: new Date().getFullYear(),
  vehicleType: "",
  vehicleTypePublicId: "",
  ownershipType: "Personal",
  occupancy: 1,
  chassisNumber: "",
  registrationDate: "",
  registrationExpiry: "",
  purchaseYear: new Date().getFullYear(),
  notes: "",
  active: true,
});

const mapApiVehicleToVehicle = (item: VehicleSearchItem): Vehicle => ({
  id: item.publicId,
  registrationNumber: item.registrationNumber || "",
  make: item.makersName || "",
  model: item.modelName || "",
  color: item.color || "",
  fuelType: item.fuelType || "",
  year: item.manufacturingMonthYear
    ? new Date(item.manufacturingMonthYear).getFullYear()
    : new Date().getFullYear(),
  vehicleType: item.vehicleTypeName || "",
  vehicleTypePublicId: item.vehicleTypePublicId || "",
  ownershipType:
    item.belongsTo?.trim().toLowerCase() === "outside" ? "Outside" : "Personal",
  occupancy: item.seatingCapacity || 1,
  chassisNumber: item.chassisNumber || "",
  registrationDate: item.registrationDate
    ? item.registrationDate.substring(0, 10)
    : "",
  registrationExpiry: item.registrationValidityDate
    ? item.registrationValidityDate.substring(0, 10)
    : "",
  purchaseYear: item.yearOfPurchase || new Date().getFullYear(),
  notes: item.notes || "",
  active: item.isActive,
});

export function VehicleMasterPage() {
  const { setPageHeader } = usePageHeader();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [search, setSearch] = useState("");
  const searchRequestId = useRef(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<Vehicle>(emptyVehicle());

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { screenName, icon: ScreenIcon } = useScreenPermission();

  const [errors, setErrors] = useState({
    registrationNumber: "",
    chassisNumber: "",
    registrationDate: "",
    registrationExpiry: "",
    make: "",
    model: "",
    color: "",
    notes: "",
    vehicleType: "",
    year: "",
    purchaseYear: "",
    occupancy: "",
    ownershipType: "",
  });

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-4 w-4" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    const loadVehicleTypes = async () => {
      try {
        const data = await getVehicleTypes();
        setVehicleTypes(data);
      } catch (error) {
        console.error("Failed to load vehicle types:", error);
        toast.error("Failed to load vehicle types");
      }
    };
    loadVehicleTypes();
  }, []);

  useEffect(() => {
    setPageNo(1);
  }, [search]);

  const searchVehicle = useCallback(
    async (searchValue: string, page: number, size: number) => {
      const value = searchValue.trim();
      if (!value) {
        return searchVehicles({
          PageNumber: page,
          PageSize: size,
        });
      }
      const requests = [
        searchVehicles({
          RegistrationNumber: value,
          PageNumber: page,
          PageSize: size,
        }),
        searchVehicles({
          ChassisNumber: value,
          PageNumber: page,
          PageSize: size,
        }),
        searchVehicles({
          MakersName: value,
          PageNumber: page,
          PageSize: size,
        }),

        searchVehicles({
          ModelName: value,
          PageNumber: page,
          PageSize: size,
        }),
      ];

      const matchingTypes = vehicleTypes.filter((type) =>
        type.typeName.toLowerCase().includes(value.toLowerCase()),
      );

      if (matchingTypes.length > 0) {
        requests.push(
          ...matchingTypes.map((type) =>
            searchVehicles({
              VehicleTypePublicId: type.publicId,
              PageNumber: page,
              PageSize: size,
            }),
          ),
        );
      }

      const responses = await Promise.all(requests);
      const successfulResponses = responses.filter(
        (response) => response.success,
      );

      const allVehicles = successfulResponses.flatMap(
        (response) => response.data,
      );
      const uniqueVehicles = Array.from(
        new Map(
          allVehicles.map((vehicle) => [vehicle.publicId, vehicle]),
        ).values(),
      );

      return {
        success: true,
        message: "",
        data: uniqueVehicles,
        errors: [],
        pageNumber: page,
        pageSize: size,
        totalRecords: uniqueVehicles.length,
        totalPages: uniqueVehicles.length
          ? Math.ceil(uniqueVehicles.length / size)
          : 0,
        hasPreviousPage: page > 1,
        hasNextPage: page < Math.ceil(uniqueVehicles.length / size),
      };
    },
    [vehicleTypes],
  );

  useEffect(() => {
    const requestId = ++searchRequestId.current;

    const loadVehicles = async () => {
      try {
        setLoading(true);
        const response = await searchVehicle(search, pageNo, pageSize);
        if (requestId !== searchRequestId.current) {
          return;
        }
        if (!response.success) {
          toast.error(
            response.message ||
              response.errors?.join(", ") ||
              "Failed to load vehicles",
          );
          setVehicles([]);
          setTotalRecords(0);
          return;
        }
        setVehicles(response.data.map(mapApiVehicleToVehicle));
        setTotalRecords(response.totalRecords);
      } catch (error) {
        if (requestId !== searchRequestId.current) {
          return;
        }
        toast.error(getApiErrorMessage(error, "Failed to load vehicles"));
        setVehicles([]);
        setTotalRecords(0);
      } finally {
        if (requestId === searchRequestId.current) {
          setLoading(false);
        }
      }
    };
    loadVehicles();
  }, [search, pageNo, pageSize, searchVehicle]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedVehicles = [...vehicles].sort((a, b) => {
    if (!sortColumn) return 0;
    let aValue = "";
    let bValue = "";
    switch (sortColumn) {
      case "registrationNumber":
        aValue = a.registrationNumber || "";
        bValue = b.registrationNumber || "";
        break;
      case "vehicleType":
        aValue = a.vehicleType || "";
        bValue = b.vehicleType || "";
        break;
      case "ownershipType":
        aValue = a.ownershipType || "";
        bValue = b.ownershipType || "";
        break;
      case "occupancy":
        aValue = String(a.occupancy || 0);
        bValue = String(b.occupancy || 0);
        break;
      case "color":
        aValue = a.color || "";
        bValue = b.color || "";
        break;
      case "active":
        aValue = a.active ? "Active" : "Inactive";
        bValue = b.active ? "Active" : "Inactive";
        break;
      default:
        return 0;
    }

    const comparison = aValue.localeCompare(bValue, undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return sortDirection === "asc" ? comparison : -comparison;

  });

  const getApiErrorMessage = (error: unknown, fallback: string): string => {
    const axiosError = error as {
      response?: {
        data?: {
          message?: string;
          errors?: string[];
        };
      };
    };

    const apiData = axiosError.response?.data;

    if (apiData?.message) {
      return apiData.message;
    }

    if (apiData?.errors?.length) {
      return apiData.errors.join(", ");
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallback;
  };

  const openAdd = () => {
    setForm(emptyVehicle());
    setErrors({
      registrationNumber: "",
      chassisNumber: "",
      registrationDate: "",
      registrationExpiry: "",
      make: "",
      model: "",
      color: "",
      notes: "",
      vehicleType: "",
      year: "",
      purchaseYear: "",
      occupancy: "",
      ownershipType: "",
    });

    setDialogOpen(true);
  };

  const REGISTRATION_NUMBER_REGEX = /^[A-Za-z0-9-]{1,30}$/;
  const CHASSIS_NUMBER_REGEX = /^[A-Za-z0-9-]{1,30}$/;
  const LETTERS_ONLY_REGEX = /^[A-Za-z ]{1,30}$/;
  const MAKE_REGEX = /^[A-Za-z -]{1,30}$/;
  const ALPHANUMERIC_REGEX = /^[A-Za-z0-9 ]*$/;

  const getTomorrowDateString = () => {
    const date = new Date();

    date.setDate(date.getDate() + 1);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const saveVehicle = async (event: FormEvent) => {
    event.preventDefault();
    const newErrors = {
      registrationNumber: "",
      chassisNumber: "",
      registrationDate: "",
      registrationExpiry: "",
      make: "",
      model: "",
      color: "",
      notes: "",
      vehicleType: "",
      year: "",
      purchaseYear: "",
      occupancy: "",
      ownershipType: "",
    };

    // Registration Number
    if (!form.registrationNumber.trim()) {
      newErrors.registrationNumber = "Registration Number is required";
    } else if (
      !REGISTRATION_NUMBER_REGEX.test(form.registrationNumber.trim())
    ) {
      newErrors.registrationNumber =
        "Registration Number can contain only letters, numbers, and hyphens (maximum 30 characters).";
    }

    // Chassis Number
    if (!form.chassisNumber?.trim()) {
      newErrors.chassisNumber = "Chassis Number is required";
    } else if (!CHASSIS_NUMBER_REGEX.test(form.chassisNumber.trim())) {
      newErrors.chassisNumber =
        "Chassis Number can contain only letters, numbers, and hyphens (maximum 30 characters).";
    }

    // Registration Date
    if (!form.registrationDate) {
      newErrors.registrationDate = "Registration Date is required";
    }

    // Make
    if (form.make && !MAKE_REGEX.test(form.make.trim())) {
      newErrors.make =
        "Make can contain only letters, spaces, and hyphens (maximum 30 characters).";
    }

    // Model
    if (form.model && !LETTERS_ONLY_REGEX.test(form.model.trim())) {
      newErrors.model =
        "Model can contain only letters and spaces (maximum 30 characters).";
    }

    // Color
    if (form.color && !LETTERS_ONLY_REGEX.test(form.color.trim())) {
      newErrors.color =
        "Color can contain only letters and spaces (maximum 30 characters).";
    }

    // Notes
    if (form.notes && !ALPHANUMERIC_REGEX.test(form.notes)) {
      newErrors.notes = "Notes can contain only letters, numbers, and spaces.";
    }

    // Registration Expiry
    if (!form.registrationExpiry) {
      newErrors.registrationExpiry = "RC Expiry Date is required";
    } else {
      const expiryDate = new Date(`${form.registrationExpiry}T00:00:00`);

      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (expiryDate < tomorrow) {
        newErrors.registrationExpiry = "RC Expiry Date must be a future date";
      }
    }

    // Vehicle Type
    if (!form.vehicleTypePublicId) {
      newErrors.vehicleType = "Vehicle Type is required";
    }

    // Manufacturing Year
    if (!form.year) {
      newErrors.year = "Manufacturing Year is required";
    }

    // Year of Purchase
    if (!form.purchaseYear) {
      newErrors.purchaseYear = "Year of Purchase is required";
    }

    // Occupancy
    if (!form.occupancy || form.occupancy < 1) {
      newErrors.occupancy = "Occupancy must be at least 1";
    }

    // Ownership
    if (!form.ownershipType) {
      newErrors.ownershipType = "Personal / Outside is required";
    }

    setErrors(newErrors);
    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    try {
      setLoading(true);

      const registrationDate = new Date(
        `${form.registrationDate}T00:00:00.000Z`,
      ).toISOString();

      const registrationValidityDate = new Date(
        `${form.registrationExpiry}T00:00:00.000Z`,
      ).toISOString();

      const manufacturingMonthYear = new Date(
        `${form.year}-01-01T00:00:00.000Z`,
      ).toISOString();

      const payload = {
        registrationNumber: form.registrationNumber.trim(),
        chassisNumber: form.chassisNumber?.trim() || "",
        registrationValidityDate,
        registrationDate,
        vehicleTypePublicId: form.vehicleTypePublicId,
        makersName: form.make.trim(),
        modelName: form.model.trim(),
        color: form.color.trim(),
        fuelType: form.fuelType || "",
        manufacturingMonthYear,
        yearOfPurchase: form.purchaseYear ?? new Date().getFullYear(),
        seatingCapacity: form.occupancy,
        belongsTo: form.ownershipType,
        notes: form.notes.trim(),

        isActive: form.active,
      };

      let response;
      if (!form.id) {
        response = await createVehicle(payload);
      } else {
        response = await updateVehicle(form.id, payload);
      }
      if (!response.success) {
        toast.error(
          response.message ||
            response.errors?.join(", ") ||
            "Failed to save vehicle",
        );

        return;
      }
      toast.success(
        response.message ||
          (form.id
            ? "Vehicle updated successfully"
            : "Vehicle created successfully"),
      );
      setDialogOpen(false);

      const refreshedResponse = await searchVehicle(search, pageNo, pageSize);

      if (refreshedResponse.success) {
        setVehicles(refreshedResponse.data.map(mapApiVehicleToVehicle));

        setTotalRecords(refreshedResponse.totalRecords);
      } else {
        toast.error(
          refreshedResponse.message ||
            refreshedResponse.errors?.join(", ") ||
            "Failed to refresh vehicles",
        );
      }

      setErrors({
        registrationNumber: "",
        chassisNumber: "",
        registrationDate: "",
        registrationExpiry: "",
        make: "",
        model: "",
        color: "",
        notes: "",
        vehicleType: "",
        year: "",
        purchaseYear: "",
        occupancy: "",
        ownershipType: "",
      });
    } catch (error) {
      console.error("Failed to save vehicle:", error);

      toast.error(
        getApiErrorMessage(
          error,
          form.id ? "Failed to update vehicle" : "Failed to create vehicle",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by registration number, chassis number, make, model, type..."
                className="h-10 pl-9 pr-9 shadow-sm"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button className="bg-[#5B9BD5] text-white" onClick={openAdd}>
              <Plus className="mr-2 h-4 w-4" /> Add Vehicle
            </Button>
          </div>
        </div>

        <Card
          className="border-[#5B9BD5]/30 bg-white"
          style={{ gap: 0, marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#E3F2FD]">
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("registrationNumber")}
                    >
                      <div className="flex items-center gap-1">
                        Vehicle
                        {sortColumn === "registrationNumber" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("vehicleType")}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortColumn === "vehicleType" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("ownershipType")}
                    >
                      <div className="flex items-center gap-1">
                        Ownership
                        {sortColumn === "ownershipType" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("occupancy")}
                    >
                      <div className="flex items-center gap-1">
                        Occupancy
                        {sortColumn === "occupancy" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="px-4 py-3 text-xs text-[#5B9BD5] cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("color")}
                    >
                      <div className="flex items-center gap-1">
                        Color
                        {sortColumn === "color" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="px-4 py-3 text-xs text-[#5B9BD5]">
                      Status
                    </TableHead>
                    <TableHead className="px-4 py-3 text-right text-xs text-[#5B9BD5]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedVehicles.map((vehicle) => (
                    <TableRow key={vehicle.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-blue-100">
                            <BusFront className="h-4 w-4 text-[#5B9BD5]" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-700">
                              {vehicle.registrationNumber}
                            </p>
                            <p className="text-[10px] text-[#5B9BD5]">
                              RC: {vehicle.chassisNumber || "—"} ·{" "}
                              {vehicle.make} {vehicle.model}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {vehicle.vehicleType}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge variant="outline">{vehicle.ownershipType}</Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {vehicle.occupancy} seats
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {vehicle.color || "—"}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge
                          className={
                            vehicle.active
                              ? "bg-green-100 text-green-700 border-green-200"
                              : "bg-gray-100 text-gray-700 border-gray-200"
                          }
                        >
                          {vehicle.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setForm({
                              ...emptyVehicle(),
                              ...vehicle,
                            });
                            setDialogOpen(true);
                          }}
                          className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!vehicles.length && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-10 text-center text-sm font-medium text-[#5B9BD5]"
                      >
                        No vehicles found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {!loading && vehicles.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <BusFront className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No vehicles found</p>
                </div>
              ) : (
                vehicles.map((vehicle) => (
                  <Card
                    key={vehicle.id}
                    className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                  >
                    <CardContent style={{ padding: "10px" }}>
                      <div className="flex items-start justify-between gap-3">
                        {/* Vehicle information */}
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center">
                            <BusFront className="w-5 h-5 text-[#5B9BD5]" />
                          </div>

                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">
                              {vehicle.registrationNumber}
                            </p>

                            <p className="text-xs text-[#5B9BD5]">
                              {vehicle.vehicleType}
                            </p>
                          </div>
                        </div>

                        {/* Status + Edit */}
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge
                            variant="outline"
                            className={
                              vehicle.active
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            }
                          >
                            {vehicle.active ? "Active" : "Inactive"}
                          </Badge>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setForm({
                                ...emptyVehicle(),
                                ...vehicle,
                              });

                              setErrors({
                                registrationNumber: "",
                                chassisNumber: "",
                                registrationDate: "",
                                registrationExpiry: "",
                                make: "",
                                model: "",
                                color: "",
                                notes: "",
                                vehicleType: "",
                                year: "",
                                purchaseYear: "",
                                occupancy: "",
                                ownershipType: "",
                              });

                              setDialogOpen(true);
                            }}
                            className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>

                      {/* Vehicle details */}
                      <div className="mt-3 space-y-1">
                        <p className="text-sm text-[#5B9BD5]">
                          RC: {vehicle.chassisNumber || "—"} · {vehicle.make}{" "}
                          {vehicle.model}
                        </p>

                        <div
                          className="flex flex-wrap items-center gap-3 text-sm text-gray-500"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span>Ownership: {vehicle.ownershipType}</span>
                          <span>Occupancy: {vehicle.occupancy} seats</span>
                        </div>

                        <div className="text-sm text-gray-500">
                          Color: <span>{vehicle.color || "—"}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>

          <Pagination
            totalItems={totalRecords}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="vehicles"
          />
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="!w-[calc(100vw-1rem)] sm:!w-[80vw] !max-w-[1200px] !max-h-[90vh] overflow-y-auto bg-white">
            <form onSubmit={saveVehicle}>
              <DialogHeader>
                <DialogTitle>
                  {form.id ? "Edit Vehicle" : "Add Vehicle"}
                </DialogTitle>
                <DialogDescription>
                  Enter vehicle details and ownership information.
                </DialogDescription>
              </DialogHeader>
              <div className="transport-floating-grid grid grid-cols-1 gap-4 py-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <InputFieldset
                    label="Registration Number"
                    required
                    value={form.registrationNumber}
                    placeholder="Enter Registration Number"
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!/^[A-Za-z0-9-]*$/.test(value)) {
                        return;
                      }

                      if (value.length > 30) {
                        return;
                      }

                      setForm({
                        ...form,
                        registrationNumber: value,
                      });

                      setErrors({
                        ...errors,
                        registrationNumber: "",
                      });
                    }}
                    className={`${
                      errors.registrationNumber
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {errors.registrationNumber && (
                    <p className="text-red-500 text-xs">
                      {errors.registrationNumber}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Chassis Number"
                    required
                    value={form.chassisNumber || ""}
                    placeholder="Enter Chassis Number"
                    maxLength={30}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!/^[A-Za-z0-9-]*$/.test(value)) {
                        return;
                      }

                      if (value.length > 30) {
                        return;
                      }

                      setForm({
                        ...form,
                        chassisNumber: value,
                      });

                      setErrors({
                        ...errors,
                        chassisNumber: "",
                      });
                    }}
                    className={`${
                      errors.chassisNumber
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {errors.chassisNumber && (
                    <p className="text-red-500 text-xs">
                      {errors.chassisNumber}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Registration Date"
                    required
                    type="date"
                    value={form.registrationDate || ""}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        registrationDate: e.target.value,
                      });

                      setErrors({
                        ...errors,
                        registrationDate: "",
                      });
                    }}
                    className={`${
                      errors.registrationDate
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {errors.registrationDate && (
                    <p className="text-red-500 text-xs">
                      {errors.registrationDate}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Registration Expiry Date"
                    required
                    type="date"
                    min={getTomorrowDateString()}
                    value={form.registrationExpiry || ""}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        registrationExpiry: e.target.value,
                      });

                      setErrors({
                        ...errors,
                        registrationExpiry: "",
                      });
                    }}
                    className={`${
                      errors.registrationExpiry
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {errors.registrationExpiry && (
                    <p className="text-red-500 text-xs">
                      {errors.registrationExpiry}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <SelectFieldset label="Vehicle Type" required>
                    <Select
                      value={form.vehicleTypePublicId}
                      onValueChange={(publicId) => {
                        const selectedType = vehicleTypes.find(
                          (item) => item.publicId === publicId,
                        );

                        setForm({
                          ...form,
                          vehicleTypePublicId: publicId,
                          vehicleType: selectedType?.typeName || "",
                        });

                        setErrors({
                          ...errors,
                          vehicleType: "",
                        });
                      }}
                    >
                      <SelectTriggerUI>
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        {vehicleTypes
                          .filter((item) => item.isActive)
                          .map((item) => (
                            <SelectItem
                              key={item.publicId}
                              value={item.publicId}
                            >
                              {item.typeName}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  {errors.vehicleType && (
                    <p className="text-red-500 text-xs">{errors.vehicleType}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Make"
                    value={form.make}
                    placeholder="Enter Make"
                    maxLength={30}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!/^[A-Za-z -]*$/.test(value)) {
                        return;
                      }

                      if (value.length > 30) {
                        return;
                      }

                      setForm({
                        ...form,
                        make: value,
                      });

                      setErrors({
                        ...errors,
                        make: "",
                      });
                    }}
                    className={`${
                      errors.make ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.make && (
                    <p className="text-red-500 text-xs">{errors.make}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Model"
                    value={form.model}
                    placeholder="Enter Model"
                    maxLength={30}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!/^[A-Za-z ]*$/.test(value)) {
                        return;
                      }

                      if (value.length > 30) {
                        return;
                      }

                      setForm({
                        ...form,
                        model: value,
                      });

                      setErrors({
                        ...errors,
                        model: "",
                      });
                    }}
                    className={`${
                      errors.model ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.model && (
                    <p className="text-red-500 text-xs">{errors.model}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Color"
                    value={form.color}
                    placeholder="Enter Color"
                    maxLength={30}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!/^[A-Za-z ]*$/.test(value)) {
                        return;
                      }

                      if (value.length > 30) {
                        return;
                      }

                      setForm({
                        ...form,
                        color: value,
                      });

                      setErrors({
                        ...errors,
                        color: "",
                      });
                    }}
                    className={`${
                      errors.color ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.color && (
                    <p className="text-red-500 text-xs">{errors.color}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <SelectFieldset label="Fuel Type">
                    <Select
                      value={form.fuelType || ""}
                      onValueChange={(value) => {
                        setForm({
                          ...form,
                          fuelType: value,
                        });
                      }}
                    >
                      <SelectTriggerUI>
                        <SelectValue placeholder="Select fuel type" />
                      </SelectTriggerUI>

                      <SelectContent>
                        <SelectItem value="Petrol">Petrol</SelectItem>
                        <SelectItem value="Diesel">Diesel</SelectItem>
                        <SelectItem value="CNG">CNG</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>
                {/* <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"> */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Manufacturing Year"
                    required
                    type="number"
                    min="1990"
                    max="2100"
                    value={form.year}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        year: Number(e.target.value),
                      });

                      setErrors({
                        ...errors,
                        year: "",
                      });
                    }}
                    className={`${
                      errors.year ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.year && (
                    <p className="text-red-500 text-xs">{errors.year}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Year of Purchase"
                    required
                    type="number"
                    min="1990"
                    max="2100"
                    value={form.purchaseYear || ""}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        purchaseYear: Number(e.target.value),
                      });

                      setErrors({
                        ...errors,
                        purchaseYear: "",
                      });
                    }}
                    className={`${
                      errors.purchaseYear
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {errors.purchaseYear && (
                    <p className="text-red-500 text-xs">
                      {errors.purchaseYear}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Occupancy"
                    required
                    placeholder="e.g., 6"
                    type="number"
                    min="1"
                    max="100"
                    value={form.occupancy}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        occupancy: Number(e.target.value),
                      });

                      setErrors({
                        ...errors,
                        occupancy: "",
                      });
                    }}
                    className={`${
                      errors.occupancy
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />

                  {errors.occupancy && (
                    <p className="text-red-500 text-xs">{errors.occupancy}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <SelectFieldset label="Personal / Outside" required>
                    <Select
                      value={form.ownershipType}
                      onValueChange={(value: OwnershipType) =>
                        setForm({
                          ...form,
                          ownershipType: value,
                        })
                      }
                    >
                      <SelectTriggerUI>
                        <SelectValue />
                      </SelectTriggerUI>
                      <SelectContent>
                        <SelectItem value="Personal">Personal</SelectItem>
                        <SelectItem value="Outside">Outside</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>
                {/* <div
                  className="space-y-2"
                  style={{ display: "flex", justifyContent: "start" }}
                >
                  <RadioGroup
                    value={form.ownershipType}
                    onValueChange={(value: OwnershipType) => {
                      setForm({
                        ...form,
                        ownershipType: value,
                        vendorId: value === "Outside" ? form.vendorId : "",
                      });

                      setErrors({
                        ...errors,
                        ownershipType: "",
                      });
                    }}
                    className="flex items-center gap-8 pt-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value="Personal"
                        id="ownership-personal"
                        className="
                          h-5 w-5 rounded-full
                          border-2 border-gray-200
                          flex items-center justify-center
                          data-[state=checked]:border-primary
                        "
                      >
                        <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                      </RadioGroupItem>

                      <label
                        htmlFor="ownership-personal"
                        className="cursor-pointer text-sm text-gray-700"
                      >
                        Personal
                      </label>
                    </div>

                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value="Outside"
                        id="ownership-outside"
                        className="
                          h-5 w-5 rounded-full
                          border-2 border-gray-200
                          flex items-center justify-center
                          data-[state=checked]:border-primary
                        "
                      >
                        <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                      </RadioGroupItem>

                      <label
                        htmlFor="ownership-outside"
                        className="cursor-pointer text-sm text-gray-700"
                      >
                        Outside
                      </label>
                    </div>
                  </RadioGroup>

                  {errors.ownershipType && (
                    <p className="text-red-500 text-xs">{errors.ownershipType}</p>
                  )}
                </div> */}
                {/* </div> */}

                <div className="sm:col-span-2 lg:col-span-3 space-y-2">
                  <TextareaFieldset
                    label="Notes"
                    value={form.notes}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (!/^[A-Za-z0-9 ]*$/.test(value)) {
                        return;
                      }

                      setForm({
                        ...form,
                        notes: value,
                      });

                      setErrors({
                        ...errors,
                        notes: "",
                      });
                    }}
                    rows={2}
                  />

                  {errors.notes && (
                    <p className="text-red-500 text-xs">{errors.notes}</p>
                  )}
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) =>
                      setForm({ ...form, active: e.target.checked })
                    }
                  />{" "}
                  Active vehicle
                </label>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    setForm(emptyVehicle());
                    setErrors({
                      registrationNumber: "",
                      chassisNumber: "",
                      registrationDate: "",
                      registrationExpiry: "",
                      make: "",
                      model: "",
                      color: "",
                      notes: "",
                      vehicleType: "",
                      year: "",
                      purchaseYear: "",
                      occupancy: "",
                      ownershipType: "",
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#5B9BD5] text-white">
                  {form.id ? "Update Vehicle" : "Save Vehicle"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
